import React, { useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Heart, MessageCircle, UserPlus, DoorOpen, DoorClosed, Trophy, ShieldAlert, Mail, Sprout, HeartHandshake } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { api, errMsg, timeAgo } from "@/lib/api";
import { useAuth } from "@/context/Auth";
import { PageHeader, Spinner, Empty, Avatar } from "@/components/Common";
import { t } from "@/i18n";

const ICONS: any = { like: Heart, comment: MessageCircle, follow: UserPlus, knock: DoorClosed, knock_accepted: DoorOpen, daily: Trophy, moderation: ShieldAlert, message: Mail, goal_reminder: Sprout, listen: HeartHandshake };
const TONE: any = { like: "magenta", comment: "cyan", follow: "violet", knock: "lime", knock_accepted: "lime", daily: "violet", moderation: "magenta", message: "cyan", goal_reminder: "lime", listen: "lime" };
const PREFS: [string, string][] = [["like", "Mi piace"], ["comment", "Commenti"], ["follow", "Nuovi follower"], ["knock", "Richieste Bussa"], ["knock_accepted", "Bussa accettati"], ["daily", "UNSEEN Daily"], ["message", "Messaggi privati"], ["goal_reminder", "Promemoria obiettivi"], ["listen", "Parla con qualcuno"]];

function NotificationItem({ n }: any) {
  const Icon = ICONS[n.type] || Heart;
  const tone = TONE[n.type] || "violet";
  return (
    <Link to={n.link || "#"} data-testid="notification-item" className={`relative flex items-start gap-3.5 px-4 py-3.5 rounded-2xl border ${!n.read ? "bg-[hsl(var(--surface-2))] border-[hsl(var(--primary)/.22)]" : "border-transparent hover:bg-[hsl(var(--surface))]"}`}>
      <div className="relative shrink-0">
        {n.actor ? <Avatar user={n.actor} size={44} /> : <span className={`w-11 h-11 rounded-full flex items-center justify-center tone-${tone}`}><Icon size={18} /></span>}
        {n.actor && <span className={`absolute -bottom-1 -right-1 w-6 h-6 rounded-full flex items-center justify-center ring-2 ring-[hsl(var(--background))] tone-${tone}`} data-testid="notification-type-icon"><Icon size={12} /></span>}
      </div>
      <div className="flex-1 min-w-0 text-sm leading-snug pt-0.5">
        {n.actor && <span className="font-semibold text-foreground">{n.actor.display_name || `@${n.actor.username}`} </span>}
        <span className="text-ink/80">{n.text}</span>
        <div className="text-xs text-muted-foreground mt-1" data-testid="notification-time">{timeAgo(n.created_at)}</div>
      </div>
      {!n.read && <span className="unread-dot mt-2" data-testid="notification-unread-dot" />}
    </Link>
  );
}

function Notifications() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["notifications"], queryFn: () => api.get("/notifications").then((r) => r.data) });
  useEffect(() => { api.post("/notifications/read").then(() => qc.invalidateQueries({ queryKey: ["unread"] })); }, [qc]);
  if (isLoading) return <Spinner />;
  if (!data.length) return <Empty testId="notifications-empty">Nessuna notifica. Quando qualcuno interagisce con te, lo vedrai qui.</Empty>;
  return <div className="space-y-1" data-testid="notifications-list">{data.map((n: any) => <NotificationItem key={n.id} n={n} />)}</div>;
}

function Knocks() {
  const qc = useQueryClient();
  const { data: inc } = useQuery({ queryKey: ["knocks-in"], queryFn: () => api.get("/bussa/incoming").then((r) => r.data) });
  const { data: out } = useQuery({ queryKey: ["knocks-out"], queryFn: () => api.get("/bussa/outgoing").then((r) => r.data) });
  const respond = async (id: string, accept: boolean) => {
    try {
      await api.post(`/bussa/requests/${id}/${accept ? "accept" : "reject"}`);
      toast.success(accept ? "Ora siete connessi" : "Richiesta rifiutata");
      qc.invalidateQueries({ queryKey: ["knocks-in"] });
      qc.invalidateQueries({ queryKey: ["unread"] });
    } catch (e) { toast.error(errMsg(e)); }
  };
  const label: any = { pending: t("bussa.sent"), accepted: t("bussa.connected"), rejected: t("bussa.rejected") };
  return (
    <div className="space-y-10">
      <section>
        <h2 className="label text-ink/62 mb-3">Hanno bussato da te</h2>
        {!inc?.length ? <Empty testId="knocks-incoming-empty">Nessuna richiesta in attesa.</Empty> : (
          <div className="divide-y divide-ink/10 border border-[hsl(var(--border))] rounded-2xl overflow-hidden bg-[hsl(var(--surface))]" data-testid="knocks-incoming">
            {inc.map((k: any) => (
              <div key={k.id} className="flex items-center gap-3 p-4" data-testid="knock-request">
                <Link to={`/u/${k.user.username}`} className="flex items-center gap-3 flex-1 min-w-0"><Avatar user={k.user} size={40} /><div className="min-w-0"><div className="font-semibold truncate">{k.user.display_name}</div><div className="text-xs text-ink/62">@{k.user.username} · {timeAgo(k.created_at)}</div></div></Link>
                <button className="btn btn-primary px-3 py-2 text-sm" onClick={() => respond(k.id, true)} data-testid="knock-accept-btn">Apri</button>
                <button className="btn btn-ghost px-3 py-2 text-sm" onClick={() => respond(k.id, false)} data-testid="knock-reject-btn">Rifiuta</button>
              </div>
            ))}
          </div>
        )}
      </section>
      <section>
        <h2 className="label text-ink/62 mb-3">Le tue richieste</h2>
        {!out?.length ? <Empty>Non hai ancora bussato a nessuno.</Empty> : (
          <div className="divide-y divide-ink/10 border border-[hsl(var(--border))] rounded-2xl overflow-hidden bg-[hsl(var(--surface))]" data-testid="knocks-outgoing">
            {out.map((k: any) => (
              <Link key={k.id} to={`/u/${k.user.username}`} className="flex items-center gap-3 p-4 hover:bg-white/60">
                <Avatar user={k.user} size={36} /><span className="flex-1 truncate">@{k.user.username}</span>
                <span className={`label ${k.status === "accepted" ? "text-foreground" : k.status === "pending" ? "text-primary" : "text-ink/56"}`} data-testid="knock-outgoing-status">{label[k.status]}</span>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function NotifSettings() {
  const { user, setUser } = useAuth();
  const prefs = user.notif_prefs || {};
  const toggle = async (k: string, v: boolean) => {
    try { const { data } = await api.patch("/me/settings", { notif_prefs: { ...prefs, [k]: v } }); setUser(data); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="divide-y divide-ink/10 border border-[hsl(var(--border))] rounded-2xl overflow-hidden bg-[hsl(var(--surface))] max-w-xl" data-testid="notif-settings">
      {PREFS.map(([k, l]) => (
        <label key={k} className="flex items-center justify-between p-4"><span>{l}</span><Switch checked={prefs[k] !== false} onCheckedChange={(v) => toggle(k, v)} data-testid={`notif-pref-${k}`} /></label>
      ))}
      <p className="p-4 text-xs text-ink/56">Le comunicazioni di moderazione vengono sempre inviate.</p>
    </div>
  );
}

export default function Attivita() {
  const [sp, setSp] = useSearchParams();
  const tab = sp.get("tab") || "notifiche";
  const tabs = [["notifiche", "Notifiche"], ["bussa", "Bussa"], ["impostazioni", "Impostazioni"]];
  return (
    <div data-testid="attivita-page">
      <PageHeader kicker="Solo eventi reali" title="Attività" />
      <div className="px-5 md:px-10 border-b border-ink/10 flex gap-6">
        {tabs.map(([k, l]) => <button key={k} onClick={() => setSp({ tab: k })} data-testid={`activity-tab-${k}`} className={`py-4 font-semibold border-b-2 transition-colors ${tab === k ? "border-primary text-foreground" : "border-transparent text-ink/62 hover:text-foreground"}`}>{l}</button>)}
      </div>
      <div className="px-5 md:px-10 py-8 max-w-3xl">{tab === "bussa" ? <Knocks /> : tab === "impostazioni" ? <NotifSettings /> : <Notifications />}</div>
    </div>
  );
}
